import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { EmptyState } from "../components/EmptyState";
import { fmtDate, fmtMoney } from "../lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const STATUS_OPTIONS = ["all", "pending", "confirmed", "cancelled", "refunded", "completed", "failed"];

export default function Bookings() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  const shouldFetch = search.trim().length > 0 || status !== "all";

  useEffect(() => {
    if (!shouldFetch) {
      setData({ items: [], total: 0 });
      setFetched(false);
      return;
    }
    setLoading(true);
    const params = { page, page_size: 25 };
    if (search) params.search = search;
    if (status !== "all") params.status = status;
    api.get("/bookings", { params })
      .then((r) => { setData(r.data); setFetched(true); })
      .finally(() => setLoading(false));
  }, [search, status, page, shouldFetch]);

  const columns = [
    { key: "reference", header: "Reference", render: (r) => <span className="font-mono text-xs" data-testid={`booking-ref-${r.id}`}>{r.reference}</span> },
    { key: "customer", header: "Customer", render: (r) => (
      <div><div className="font-medium">{r.customer_name}</div><div className="text-xs text-muted-foreground">{r.customer_email}</div></div>
    )},
    { key: "experience", header: "Experience", render: (r) => (
      <div className="max-w-[260px] truncate"><div className="text-sm">{r.experience_title}</div><div className="text-xs text-muted-foreground">{r.supplier_name}</div></div>
    )},
    { key: "activity", header: "Activity", render: (r) => (
      <div className="text-xs"><div>{r.activity_date}</div><div className="text-muted-foreground">{r.activity_time}</div></div>
    )},
    { key: "travelers_count", header: "Pax", render: (r) => <span className="tabular-nums">{r.travelers_count}</span> },
    { key: "amount_paid", header: "Amount", render: (r) => <span className="tabular-nums">{fmtMoney(r.amount_paid, r.currency)}</span> },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "payment_status", header: "Payment", render: (r) => <StatusBadge status={r.payment_status} /> },
  ];

  return (
    <div data-testid="bookings-page">
      <PageHeader title="Bookings" description="On-demand search — type a reference, customer, experience or supplier to fetch matching bookings, or pick a status." />
      <div className="pt-6">
        {!shouldFetch ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px] max-w-sm">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search reference, customer, experience, supplier…"
                  className="w-full h-9 text-sm rounded-md border border-input bg-background px-3 py-1"
                  data-testid="bookings-search-input"
                />
              </div>
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="bookings-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <EmptyState
              icon={Search}
              title="Search to begin"
              description="Bookings load on demand. Type at least one character above, or pick a status to fetch results."
              testId="bookings-empty"
            />
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={data.items}
            total={data.total}
            page={page}
            pageSize={25}
            loading={loading}
            onPageChange={setPage}
            searchValue={search}
            onSearchChange={(v) => { setSearch(v); setPage(1); }}
            searchPlaceholder="Search reference, customer, experience, supplier…"
            toolbar={
              <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
                <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="bookings-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            }
            onRowClick={(r) => nav(`/bookings/${r.id}`)}
            emptyTitle="No bookings match"
            emptyDescription="Try a different search term or status filter."
            testIdPrefix="bookings"
          />
        )}
      </div>
    </div>
  );
}
